/**
 * «Контакты» в правой панели чата: номера клиента и какая связь с ним установлена.
 * Сверху чипы номеров (активный — по нему ищется сделка; «+» — привязать номер руками), под ними
 * полоска: личный кабинет (вошёл ли клиент на my.uni-renter.ru — «авторизован») и каналы
 * WhatsApp · Telegram · MAX · Push · Почта (+ Авито/ВК/Instagram, если клиент там писал) в трёх
 * состояниях: подключён — пишем из кабинета; есть зацепка — аккаунт известен, но
 * отсюда не написать; нет. Состояние читается формой значка и рамки, не только цветом. Ниже —
 * строки только по каналам, где есть что сказать; строка канала с чатом — ссылка в этот чат,
 * связь через бота подписана «TG-бот» / «MAX-бот». Факты — `GET /api/chats/{id}/contacts`.
 * Спека `docs/specs/2026-10-03-chat-contacts-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { BellRing, Circle, CircleCheck, CircleDashed, Mail, Phone, Plus, UserRound, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link, useLocation } from "react-router";

import { dt } from "@/components/timeline/Timeline";
import {
  type ChatPhone,
  type ClientContacts,
  type ContactBot,
  type ContactLast,
  type ContactLevel,
  loadChatContacts,
  regionTitle,
} from "@/lib/clientChats";

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3 text-[12.5px]";
const chip = "rounded-[var(--radius-ui)] border px-2 py-0.5 text-[12px]";
const on = "border-accent text-fg";
const off = "border-border text-fg-muted hover:text-fg";
const input = "h-7 rounded-[var(--radius-ui)] border border-border bg-transparent px-2 text-[12px]";
const ghost = "h-7 rounded-[var(--radius-ui)] border border-border px-2 text-[12px] text-fg-muted hover:bg-bg-hover disabled:opacity-50";

export const fmtPhone = (p: string) =>
  p.length === 11 ? `+${p[0]} ${p.slice(1, 4)} ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(9)}` : p;

/** Номер в строке статуса не рвём переносом: неразрывные пробелы и дефисы. */
const nbPhone = (p: string) => fmtPhone(p).replace(/ /g, "\u00a0").replace(/-/g, "\u2011");

type ChannelId = "cabinet" | "whatsapp" | "telegram" | "max" | "push" | "email" | "avito" | "vk" | "instagram";
const LEVEL: Record<ContactLevel, { box: string; Icon: typeof Circle; icon: string; sr: string }> = {
  ok: { box: "border-ok/60 bg-ok-bg text-fg", Icon: CircleCheck, icon: "text-ok", sr: "подключён" },
  partial: { box: "border-warn/60 bg-warn-bg text-fg", Icon: CircleDashed, icon: "text-warn", sr: "есть, но не подключён" },
  none: { box: "border-dashed border-border text-fg-muted", Icon: Circle, icon: "text-fg-faint", sr: "нет" },
};
/** Цвет и буква — как метка канала на аватаре в списке (`CHANNELS` в ChatsPage); кабинет, push и
 * почта — значки. `short` — подпись бейджа, как у вкладок списка: шесть бейджей встают в строку. */
const MARK: Record<ChannelId, { label: string; short?: string; mark?: string; color?: string }> = {
  cabinet: { label: "Кабинет" },
  whatsapp: { label: "WhatsApp", short: "WA", mark: "W", color: "#25d366" },
  telegram: { label: "Telegram", short: "TG", mark: "T", color: "#2aabee" },
  max: { label: "MAX", mark: "M", color: "#7c4dff" },
  push: { label: "Push" },
  email: { label: "Почта" },
  avito: { label: "Авито", mark: "A", color: "#00a046" },
  vk: { label: "ВК", mark: "В", color: "#0077ff" },
  instagram: { label: "Инста", mark: "I", color: "#e1306c" },
};

/** Строка канала: `status` пуст — сказать нечего, канал виден только бейджем. `label` — своя
 * подпись строки: «TG-бот» / «MAX-бот», когда связь и авторизация — через бота, а не аккаунт. */
type Row = { id: ChannelId; level: ContactLevel; status: string; detail: string; chatId: number | null; hint: string; label?: string };
const BOT_LABEL = { telegram: "TG-бот", max: "MAX-бот" } as const;

const who = (m: ContactLast) => (m ? `${m.out ? "от нас" : "от клиента"} ${dt(m.date)}` : "");
const day = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}` : "");
const join = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(" · ");
const devices = (n: number) =>
  `${n} ${n % 10 === 1 && n % 100 !== 11 ? "устройство" : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? "устройства" : "устройств"}`;
const BROWSER_HINT = "Личный аккаунт сотрудника, переписка импортирована расширением Chrome. Отсюда не отправить.";

const VIA: Record<string, string> = { telegram: "Telegram", max: "MAX", push: "push" };

/** Личный кабинет: номер авторизован, когда клиент сам ввёл его на my.uni-renter.ru/login и
 * подтвердил ботом или push; на примерке номер ещё и сверен с карточкой клиента. */
function cabinetRow(c: ClientContacts["cabinet"], manyPhones: boolean): Row {
  const base = { id: "cabinet" as const, level: c.level, chatId: null };
  if (c.level === "ok")
    return {
      ...base,
      status: `авторизован · ${dt(c.last)}`,
      detail: join(
        c.channels.length > 0 && `через ${c.channels.map((k) => VIA[k] ?? k).join(", ")}`,
        c.fitting && `на примерке${c.fitting.stylist ? `, стилист ${c.fitting.stylist}` : ""}`,
        manyPhones && c.phones.map(nbPhone).join(", "),
        c.since && c.since !== c.last && `впервые ${day(c.since)}`,
      ),
      hint: "Клиент вошёл в личный кабинет my.uni-renter.ru: сам ввёл номер и подтвердил его ботом или push.",
    };
  if (c.pending)
    return {
      ...base,
      status: "начал вход, не закончил",
      detail: dt(c.pending),
      hint: "Клиент ввёл номер на my.uni-renter.ru/login, но не подтвердил его — бота не запустил и push не включил.",
    };
  return {
    ...base,
    status: "",
    detail: "",
    hint: "В личный кабинет не входил. Клиент вводит номер на my.uni-renter.ru/login и подтверждает его ботом Telegram, MAX или push.",
  };
}

function whatsappRow(c: ClientContacts["whatsapp"], hasPhone: boolean): Row {
  const base = { id: "whatsapp" as const, level: c.level };
  if (c.ours) {
    const w = c.ours.window;
    return {
      ...base,
      chatId: c.ours.chat_id,
      status: w.open
        ? `наш номер · окно до ${dt(w.until)}`
        : `наш номер · ${w.last_in ? "окно закрыто" : "клиент не отвечал"}, только шаблон`,
      detail: who(c.ours.last),
      hint: w.open
        ? "Переписка через наш номер WhatsApp. Окно 24 ч открыто — можно писать свободным текстом."
        : "Переписка через наш номер WhatsApp. Клиент не писал больше 24 ч — свободный текст не уйдёт, только шаблон.",
    };
  }
  if (c.browser)
    return {
      ...base,
      chatId: c.browser.chat_id,
      status: "переписка из браузера",
      detail: join(c.browser.account_name && `аккаунт «${c.browser.account_name}»`, who(c.browser.last), "только чтение"),
      hint: BROWSER_HINT,
    };
  return {
    ...base,
    chatId: null,
    status: "",
    detail: "",
    hint: hasPhone
      ? "Через наш номер WhatsApp клиент не писал."
      : "Номера нет — привяжите, чтобы написать в WhatsApp.",
  };
}

function botRow(id: "telegram" | "max", c: ContactBot, manyPhones: boolean): Row {
  const base = { id, level: c.level };
  const app = MARK[id].label;
  const bot = id === "telegram" ? "@unirenterbot" : "бота линии MAX";
  const authPhone = c.auth && manyPhones ? ` · ${nbPhone(c.auth.phone)}` : "";
  if (c.bot) {
    const nick = c.bot.username ? `@${c.bot.username}` : c.bot.name;
    return {
      ...base,
      label: BOT_LABEL[id],
      chatId: c.bot.chat_id,
      status: `бот запущен · ${c.auth ? "авторизован" : "не авторизован"}${authPhone}`,
      detail: join(
        nick,
        who(c.bot.last),
        c.bot.via === "deal" && `уведомления по сделке ${c.bot.deal_id}`,
        c.bot.via === "zapis" && "пришёл по ссылке на запись",
      ),
      hint: c.auth
        ? `Клиент запустил ${bot} — пишем из кабинета. Авторизован: через этот бот вошёл в личный кабинет и сам подтвердил номер.`
        : `Клиент запустил ${bot} — пишем из кабинета. Не авторизован: в личный кабинет через этот бот не входил, номер за аккаунтом ${app} не подтверждён.`,
    };
  }
  if (c.auth)
    return {
      ...base,
      label: BOT_LABEL[id],
      chatId: null,
      status: `авторизован · чата с ботом нет${authPhone}`,
      detail: `вошёл в личный кабинет ${dt(c.auth.since)}`,
      hint: `Клиент вошёл в личный кабинет через бота ${app}, но чата с ботом у нас нет — из кабинета пока не написать.`,
    };
  if (c.account.state === "yes")
    return {
      ...base,
      chatId: null,
      status: "есть аккаунт · бот не запущен",
      detail: join(
        c.account.username && `@${c.account.username}`,
        c.account.source === "crm"
          ? id === "max"
            ? "CRM писала клиенту в MAX"
            : "по связке CRM"
          : c.account.checked && `проверено ${day(c.account.checked)}`,
      ),
      hint: `${c.account.source === "crm" ? `По данным CRM за номером есть ${app}` : `На номере есть ${app}`}. Бота клиент не запускал — из кабинета не написать. Кнопка ${app} есть в ссылке на запись и на бронь.`,
    };
  if (c.browser)
    return {
      ...base,
      chatId: c.browser.chat_id,
      status: "переписка из браузера",
      detail: join(c.browser.account_name && `аккаунт «${c.browser.account_name}»`, who(c.browser.last), "только чтение"),
      hint: BROWSER_HINT,
    };
  if (c.account.state === "no")
    return {
      ...base,
      chatId: null,
      status: `на номере нет ${app}`,
      detail: c.account.checked ? `проверено ${day(c.account.checked)}` : "",
      hint: `Проверяли своим аккаунтом: ${app} на номере нет. Звать в бота бессмысленно — используйте другой канал.`,
    };
  return {
    ...base,
    chatId: null,
    status: "",
    detail: "",
    hint: `Бота не запускал, есть ли ${app} на номере — не проверяли. Клиент подключается сам — кнопка ${app} в ссылке на запись или на бронь.`,
  };
}

function pushRow(c: ClientContacts["push"]): Row {
  const base = { id: "push" as const, level: c.level, chatId: null };
  if (c.live.length > 0) {
    const delivered = c.live.map((d) => d.delivered ?? "").sort().at(-1);
    const since = c.live.map((d) => d.since ?? "").filter(Boolean).sort()[0];
    return {
      ...base,
      status: devices(c.live.length),
      detail: join(
        [...new Set(c.live.map((d) => d.device))].join(", "),
        delivered ? `доставлено ${dt(delivered)}` : "ещё не доставляли",
        since && `с ${day(since)}`,
      ),
      hint: "Web Push со страницы брони или записи — уведомления приходят на эти устройства.",
    };
  }
  if (c.refused.count > 0)
    return {
      ...base,
      status: `отказал в разрешении${c.refused.count > 1 ? ` ×${c.refused.count}` : ""}`,
      detail: `последний раз ${dt(c.refused.last)}`,
      hint: "Клиент видел запрос на уведомления и отказал. Живой подписки нет.",
    };
  return { ...base, status: "", detail: "", hint: "Подписки нет. Клиент подписывается сам на странице брони или записи." };
}

function toRows(c: ClientContacts, phones: number): Row[] {
  return [
    cabinetRow(c.cabinet, phones > 1),
    whatsappRow(c.whatsapp, phones > 0),
    botRow("telegram", c.telegram, phones > 1),
    botRow("max", c.max, phones > 1),
    pushRow(c.push),
    {
      id: "email",
      level: c.email.level,
      chatId: null,
      status: c.email.addresses.join(" · "),
      detail: "",
      hint: c.email.addresses.length
        ? "Адрес клиент оставил на странице брони. Писем пока не шлём."
        : "Адреса нет — клиент оставляет его на странице брони.",
    },
    ...c.marketplaces.map(
      (m): Row => ({
        id: m.id,
        level: "ok",
        chatId: m.chat_id,
        status: who(m.last) || "чат есть",
        detail: m.context,
        hint: "Чат клиента на площадке — ответить можно из кабинета.",
      }),
    ),
  ];
}

function Mark({ id, dim }: { id: ChannelId; dim: boolean }) {
  const m = MARK[id];
  const cls = `flex-none ${dim ? "opacity-50" : ""}`;
  if (id === "cabinet") return <UserRound size={14} aria-hidden className={`${cls} text-fg-muted`} />;
  if (id === "push") return <BellRing size={14} aria-hidden className={`${cls} text-fg-muted`} />;
  if (id === "email") return <Mail size={14} aria-hidden className={`${cls} text-fg-muted`} />;
  return (
    <span
      aria-hidden
      className={`grid size-3.5 place-items-center rounded-full text-[9px] font-semibold leading-none text-white ${cls}`}
      style={{ background: m.color }}
    >
      {m.mark}
    </span>
  );
}

function Badge({ row }: { row: Row }) {
  const l = LEVEL[row.level];
  return (
    <li
      title={row.hint}
      className={`inline-flex h-6 items-center gap-1 rounded-[var(--radius-ui)] border px-1 text-[12px] ${l.box}`}
    >
      <Mark id={row.id} dim={row.level === "none"} />
      {MARK[row.id].short ?? MARK[row.id].label}
      {/* «Нет» — пунктирная рамка без значка: пять бейджей типичного клиента встают в одну строку. */}
      {row.level !== "none" && <l.Icon size={12} aria-hidden className={l.icon} />}
      <span className="sr-only">: {l.sr}</span>
    </li>
  );
}

/** Строка канала. Есть чат в этом канале — вся строка ведёт в него: щёлкнул и пишешь туда. */
function ChannelRow({ row, chatId }: { row: Row; chatId: number }) {
  const { search } = useLocation();
  const grid = "grid grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-2";
  const body = (
    <>
      <div className="flex items-center gap-1.5 pt-0.5">
        <Mark id={row.id} dim={row.level === "none"} />
        <span className="w-[62px] font-medium">{row.label ?? MARK[row.id].label}</span>
      </div>
      <div className="min-w-0">
        <div className={row.level === "none" ? "text-fg-muted" : ""}>{row.status}</div>
        {row.detail && <div className="break-words text-fg-muted">{row.detail}</div>}
      </div>
    </>
  );
  if (row.chatId === null || row.chatId === chatId)
    return (
      <li className={`${grid} px-1.5 py-0.5`}>
        {body}
        {row.chatId !== null && <span className="text-fg-muted">этот чат</span>}
      </li>
    );
  return (
    <li>
      <Link
        to={{ pathname: `/chats/${row.chatId}`, search }}
        title={`Открыть чат: ${row.label ?? MARK[row.id].label}`}
        className={`${grid} rounded-[var(--radius-ui)] px-1.5 py-0.5 hover:bg-bg-hover focus-visible:outline-2 focus-visible:outline-accent`}
      >
        {body}
        <span className="text-accent-text">чат →</span>
      </Link>
    </li>
  );
}

const phoneHint = (p: ChatPhone) =>
  p.source === "manual"
    ? p.user?.startsWith("из ") // подставил сам кабинет: «из сделки N» (`chat/deal.номер_клиента`)
      ? `${p.user}, ${dt(p.date)}`
      : `привязал(а) ${p.user || "сотрудник"} ${dt(p.date)}`
    : p.source === "chat"
      ? "номер WhatsApp клиента"
      : `клиент написал ${dt(p.date)}`;

export function Contacts({
  chatId,
  phones,
  active,
  onPick,
  onAttach,
  onDetach,
  busy,
  actions,
  footer,
}: {
  chatId: number;
  phones: ChatPhone[];
  active: string | null;
  onPick: (phone: string) => void;
  onAttach: (phone: string) => void;
  onDetach: (phone: string) => void;
  busy: boolean;
  /** «проверить в Bitrix» · «поиск →» — живут в панели сделки. */
  actions?: ReactNode;
  /** Ход поиска сделки по номеру и ошибки привязки — внизу, чтобы не дёргать полоску. */
  footer?: ReactNode;
}) {
  const [add, setAdd] = useState(false);
  const [draft, setDraft] = useState("");
  const [copied, setCopied] = useState<string | null>(null);
  // Клик по номеру — и выбрать его (сделка по этому номеру), и взять в буфер: «+79816855958»,
  // без пробелов — так его принимают поиск, CRM и мессенджеры. Буфер недоступен — молчим.
  const pick = (phone: string) => {
    onPick(phone);
    void navigator.clipboard?.writeText(`+${phone}`).then(
      () => {
        setCopied(phone);
        setTimeout(() => setCopied((c) => (c === phone ? null : c)), 1500);
      },
      () => undefined,
    );
  };
  const q = useQuery({
    queryKey: ["chat-contacts", chatId],
    queryFn: () => loadChatContacts(chatId),
    staleTime: 60_000,
  });
  const rows = q.data ? toRows(q.data, phones.length) : [];
  const told = rows.filter((r) => r.status);
  return (
    <div className={card} data-annot="контакты клиента">
      <div className="mb-1 flex items-baseline gap-2">
        <span className="text-[13px] font-medium">Контакты</span>
        <span className="ml-auto flex gap-2">{actions}</span>
      </div>

      <div className="flex flex-wrap items-center gap-1" data-annot="номера клиента">
        {phones.length === 0 && <span className="text-fg-muted">Номера нет — клиент его не писал.</span>}
        {phones.map((p) => (
          <span key={p.phone} className={`inline-flex items-center gap-1 font-mono ${chip} ${p.phone === active ? on : off}`}>
            <button
              type="button"
              onClick={() => pick(p.phone)}
              title={`${phoneHint(p)} · клик — скопировать номер`}
              aria-pressed={p.phone === active}
              className="relative"
            >
              {/* Отметка поверх номера, а не вместо него: ширина плашки не прыгает. */}
              <span className={copied === p.phone ? "invisible" : ""}>{fmtPhone(p.phone)}</span>
              {copied === p.phone && (
                <span role="status" className="absolute inset-0 text-center font-sans text-ok">
                  скопирован
                </span>
              )}
            </button>
            {/* Откуда номер — коротко, город или область; целиком и салон — в подсказке. */}
            {p.region && (
              <span className="font-sans text-[11px] opacity-75" title={regionTitle(p.region)}>
                {p.region.text.split(", ")[0]}
              </span>
            )}
            {/* Звонок — ссылкой `sip:`, как «Звонить» в «Обзвоне»: набирает софтфон сотрудника (MicroSIP). */}
            <a
              href={`sip:+${p.phone}`}
              title={`Позвонить ${fmtPhone(p.phone)} — звонок наберёт софтфон (MicroSIP)`}
              className="text-[#2e7d32] hover:text-[#256b29]"
              data-annot="звонить на номер"
            >
              <Phone size={12} aria-hidden />
              <span className="sr-only">Позвонить {fmtPhone(p.phone)}</span>
            </a>
            {p.source === "manual" && (
              <button
                type="button"
                onClick={() => onDetach(p.phone)}
                disabled={busy}
                title="Отвязать"
                className="text-fg-faint hover:text-err"
              >
                <X size={12} aria-hidden />
                <span className="sr-only">Отвязать {fmtPhone(p.phone)}</span>
              </button>
            )}
          </span>
        ))}
        <button
          type="button"
          onClick={() => setAdd((v) => !v)}
          aria-expanded={add}
          title="Привязать номер руками"
          className={`grid size-6 place-items-center rounded-full border ${
            add ? "border-accent text-accent-text" : "border-border text-fg-muted hover:bg-bg-hover hover:text-fg"
          }`}
        >
          <Plus size={14} aria-hidden />
          <span className="sr-only">Привязать номер</span>
        </button>
      </div>
      {add && (
        <form
          className="mt-2 flex gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (!draft.trim()) return;
            onAttach(draft.trim());
            setDraft("");
            setAdd(false);
          }}
        >
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Escape" && setAdd(false)}
            placeholder="+7 900 000-00-00"
            aria-label="Привязать номер к чату"
            autoFocus
            className={`${input} min-w-0 flex-1 font-mono`}
          />
          <button type="submit" disabled={!draft.trim() || busy} className={ghost}>
            Привязать
          </button>
          <button type="button" onClick={() => setAdd(false)} className={ghost}>
            отмена
          </button>
        </form>
      )}

      {q.data && (
        <ul aria-label="Каналы связи" className="mt-2 flex flex-wrap gap-1" data-annot="каналы связи">
          {rows.map((r) => (
            <Badge key={r.id} row={r} />
          ))}
        </ul>
      )}
      {told.length > 0 && (
        <ul className="-mx-1.5 mt-2 flex flex-col gap-0.5 border-t border-border pt-1.5" data-annot="связь по каналам">
          {told.map((r) => (
            <ChannelRow key={r.id} row={r} chatId={chatId} />
          ))}
        </ul>
      )}
      {q.isError && <div className="mt-1 text-warn">Каналы связи не загрузились: {q.error.message}</div>}

      {footer}
    </div>
  );
}
